import { Link } from "react-router-dom";
import {
  Shield, Fingerprint, FileCheck2, Database, Scale, ScrollText, ArrowUpRight,
} from "lucide-react";
import { Section, Eyebrow, H1, H2, P, Status, Card } from "@/components/SharedUI";
import Term, { TERMS } from "@/components/Term";
import TrustBar from "@/components/TrustBar";

function ProductCard({ icon: Icon, title, status, desc, to, ctaLabel }) {
  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="h-14 w-14 border border-stone-200 bg-stone-50 flex items-center justify-center">
          <Icon className="h-7 w-7 text-[#9C4221]" strokeWidth={1.5} />
        </div>
        <Status tone="accent">{status}</Status>
      </div>
      <h3 className="font-display font-bold text-2xl tracking-tight text-stone-900">{title}</h3>
      <p className="mt-3 text-sm text-stone-600 leading-relaxed min-h-20">{desc}</p>
      <Link
        to={to}
        data-testid={`product-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-stone-900 border-b border-stone-300 pb-1 hover:border-stone-900 transition-colors"
      >
        {ctaLabel} <ArrowUpRight className="h-4 w-4" />
      </Link>
    </Card>
  );
}

const SPINY_SPHERE_IMG = "https://customer-assets.emergentagent.com/job_bigibila-react/artifacts/wgfjrskc_1000019640.jpg";

function SpinySphere() {
  return (
    <figure className="relative mx-auto w-full max-w-[640px]" data-testid="home-spiny-sphere">
      <div className="relative aspect-square overflow-hidden bg-stone-950 border border-stone-900 shadow-2xl">
        <img
          src={SPINY_SPHERE_IMG}
          alt="BIGIBILA Spiny Sphere™ — Indigenous Data Governance Architecture. Concentric rings of Lore, Governance, Community, Permissions, Metadata and Systems around a Sacred Knowledge / Cultural Authority core; external spines for FPIC, ICIP, IDS, Escalation and Access Control."
          className="h-full w-full object-cover"
          loading="eager"
        />
      </div>
      <figcaption className="mt-4 text-[10px] tracking-[0.24em] uppercase font-bold text-stone-500 text-center">
        Bigibila Spiny Sphere™ · Indigenous Data Governance Architecture
      </figcaption>
    </figure>
  );
}

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="border-b border-stone-200 relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="rise-in text-[11px] tracking-[0.32em] uppercase font-semibold text-[#9C4221] mb-3">
              Yaamagara <span className="text-stone-500 font-normal normal-case tracking-normal italic">— "How are you" in Gamilaraay</span>
            </div>
            <div className="rise-in"><Eyebrow>Indigenous governance technology</Eyebrow></div>
            <div className="rise-in delay-1">
              <H1>
                Turn cultural protection into<br />
                <span className="text-stone-400">working software.</span>
              </H1>
            </div>
            <div className="rise-in delay-2">
              <P className="mt-8 max-w-xl">
                BIGIBILA SYSTEMS™ takes the principles behind <Term def={TERMS.ICIP}>ICIP</Term>, <Term def={TERMS.IDS}>IDS</Term>, <Term def={TERMS.FPIC}>FPIC</Term> and <Term def={TERMS.UNDRIP}>UNDRIP</Term> and makes them real: practical software controls, governance architecture and evidence you can show a board. <span className="text-stone-500">(Hover any underlined term for a plain-English definition.)</span>
              </P>
            </div>
            <div className="rise-in delay-3 mt-6 max-w-xl border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-4">
              <p className="text-sm font-medium text-stone-800 leading-relaxed">
                Honouring the old people, Bigibila, and the quills that teach protection, boundary, consequence and survival.
              </p>
            </div>
            <div className="rise-in delay-4 mt-10 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="home-request-demo-btn"
                className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
              >
                Request a Demo <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/story"
                data-testid="home-explore-story-btn"
                className="inline-flex items-center gap-2 border border-stone-300 px-6 py-3.5 text-sm font-medium rounded-sm hover:border-stone-900 transition-colors"
              >
                Enter Through Story
              </Link>
            </div>
            <div className="rise-in delay-4 mt-10 grid grid-cols-3 gap-3 max-w-xl">
              {["Background IP protected", "Cultural authority led", "Enterprise licence pathway"].map((x) => (
                <div key={x} className="border border-stone-200 px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-600 leading-tight">
                  {x}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 rise-in delay-2">
            <SpinySphere />
            <div className="mt-6 text-center text-[10px] font-bold uppercase tracking-[0.22em] text-stone-500">
              Strong foundations · Living governance · Sovereign futures
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR — compliance & authority signals */}
      <TrustBar />

      {/* ACKNOWLEDGEMENT OF COUNTRY */}
      <section className="border-b border-stone-200 bg-stone-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-3">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-[#9C4221] rounded-full" />
              Acknowledgement of Country
            </div>
          </div>
          <div className="lg:col-span-9">
            <p className="font-display text-xl md:text-2xl tracking-tight text-stone-900 leading-snug max-w-3xl">
              We acknowledge the Traditional Custodians of the lands and waters across Australia, and pay our respect to Elders past, present and emerging. We honour the unbroken connection of Aboriginal and Torres Strait Islander peoples to Country, Lore, story and kinship — and we recognise that sovereignty was never ceded.
            </p>
          </div>
        </div>
      </section>

      {/* PRODUCT FRAMEWORKS — high-level, request-only */}
      <section className="border-b border-stone-200">
        <Section>
          <Eyebrow>Products & frameworks</Eyebrow>
          <H2>Sovereign governance frameworks — available on request, under Background IP.</H2>
          <p className="mt-6 max-w-3xl text-stone-700 leading-relaxed">
            Detailed methodologies, tools and platform components are held under our Background IP notice and provided only through direct engagement. Board-level briefings, capability statements and licensing pathways available on request.
          </p>
          <div className="mt-10 grid gap-px bg-stone-200 border border-stone-200 lg:grid-cols-2">
            <ProductCard
              icon={Shield}
              title="BIGIBILA SYSTEMS™ Platform"
              status="By request"
              desc="Protected governance software supporting cultural authority, ICIP, IDS, consent workflows, access controls and audit evidence — for accredited pilots only."
              to="/contact"
              ctaLabel="Request Access"
            />
            <ProductCard
              icon={ScrollText}
              title="Aboriginal Governance Architecture Toolkit™"
              status="By request"
              desc="A protected suite of templates, registers, matrices and implementation packs — engagement scoped and licensed under Background IP."
              to="/contact"
              ctaLabel="Request Access"
            />
          </div>
        </Section>
      </section>

      {/* PRODUCT PROMISE */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <Eyebrow>Product promise</Eyebrow>
              <H2 className="text-3xl md:text-4xl">
                A governance system for authority, permission, protection, accountability and evidence.
              </H2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <P>
                BIGIBILA SYSTEMS™ is not a database for extracting, owning or centralising Aboriginal cultural knowledge. It is designed to support governance controls around authority, permission, protection and auditable decision-making.
              </P>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
            {[
              [Fingerprint, "ICIP controls", "Asset registers, use conditions, consent evidence and takedown pathways."],
              [Database, "IDS controls", "Provenance, custodianship, access rules and sovereignty risk indicators."],
              [FileCheck2, "FPIC workflows", "Requests, authority review, approval, refusal, withdrawal and renewal."],
              [Scale, "UNDRIP alignment", "Participation, self-determination, benefit, accountability and remedy checks."],
            ].map(([Icon, title, desc]) => (
              <div key={title} className="bg-[#FAFAF9] p-7 hover-rise" data-testid={`promise-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon className="h-5 w-5 text-[#9C4221]" strokeWidth={1.5} />
                <h3 className="mt-5 font-display font-bold text-xl tracking-tight text-stone-900">{title}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* FOUNDER — Liana Pitt */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <Eyebrow>Founder</Eyebrow>
              <div className="aspect-[4/5] border border-stone-200 bg-stone-100 overflow-hidden relative" data-testid="home-founder-portrait">
                <div className="absolute inset-0 bg-gradient-to-br from-[#9C4221]/[0.08] via-stone-50 to-stone-100" />
                <img
                  src="https://customer-assets.emergentagent.com/job_bigibila-react/artifacts/vcrxuyk9_1780003247152.png"
                  alt="Liana Pitt with her Nan, Barbara Helen Fernando — three generations of Yuwaalaraay/Gamilaraay matrilineal responsibility."
                  className="relative h-full w-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-stone-500">
                Liana Pitt with her Nan, <span className="text-stone-700">Barbara Helen Fernando</span> — three generations of matrilineal responsibility.
              </p>
            </div>
            <div className="lg:col-span-8">
              <H2 className="text-3xl md:text-5xl">Liana Pitt</H2>
              <div className="mt-3 text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221]">
                Gamilaraay woman · Governance Architect · Founder
              </div>

              <p className="mt-6 text-stone-800 leading-[1.85] text-lg max-w-3xl">
                Liana Pitt is a Gamilaraay woman, governance architect, and founder of Bigibila Consultancy Firm. She is the creator of the Bigibila Advisory Method™, Lore-Led Architecture™, and the Aboriginal Governance Architecture Model™ — a suite of Indigenous governance frameworks designed to operationalise cultural authority, stewardship, knowledge governance, and community-led decision-making across policy, programs, and systems.
              </p>
              <p className="mt-5 text-stone-700 leading-[1.85] max-w-3xl">
                Drawing on experience across government, community, and justice sectors, Liana works with organisations seeking to strengthen governance, embed cultural intelligence, and design systems that create lasting impact for Aboriginal communities and future generations.
              </p>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200 max-w-2xl">
                {[
                  ["Bigibila Advisory Method™", "Created"],
                  ["Lore-Led Architecture™", "Created"],
                  ["Aboriginal Governance Architecture Model™", "Created"],
                ].map(([t, role]) => (
                  <div key={t} className="bg-[#FAFAF9] p-4">
                    <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-stone-500">{role}</div>
                    <div className="mt-1 font-display font-bold text-sm text-stone-900 leading-snug">{t}</div>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                data-testid="home-founder-contact-btn"
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-stone-900 border-b border-stone-300 pb-1 hover:border-stone-900 transition-colors"
              >
                Request a discovery call with Liana <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Section>
      </section>

      {/* CLOSING — Bigibila statement (cream) */}
      <section className="border-b border-stone-200">
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>Built quietly. Built right.</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-bold text-stone-900 leading-tight">
              Bigibila is not branding.<br />
              <span className="text-[#9C4221]">It is memory, method and obligation.</span>
            </h2>
            <p className="mt-6 text-stone-700 leading-relaxed max-w-lg">
              The quills are the governance logic. Protection. Boundary. Consequence. Survival.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/story" data-testid="home-story-btn" className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors">
                Enter through Story
              </Link>
              <Link to="/assurance" data-testid="home-assurance-btn" className="inline-flex items-center gap-2 border border-stone-300 text-stone-900 px-6 py-3.5 text-sm font-medium rounded-sm hover:border-stone-900 transition-colors">
                Community Responsibility
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-stone-200 border border-stone-200">
            {[
              { k: "Authority", v: "Mapped" },
              { k: "Consent", v: "Recorded" },
              { k: "Custody", v: "Honoured" },
              { k: "Audit", v: "Cryptographic" },
              { k: "Hosting", v: "Sovereign" },
              { k: "Release", v: "Revocable" },
            ].map((cell) => (
              <div key={cell.k} className="bg-[#FAFAF9] p-6">
                <div className="text-[10px] tracking-[0.28em] uppercase text-stone-500 font-bold">{cell.k}</div>
                <div className="mt-2 font-display text-2xl tracking-tight text-stone-900">{cell.v}</div>
              </div>
            ))}
          </div>
        </Section>
      </section>
    </>
  );
}
